/**
 * Starter templates — T3.15/T3.16.
 *
 * A template is a bundled Yjs snapshot (PS1: no network fetch) authored in the
 * editor itself through builder-mcp (`export_template`) and committed under
 * `./templates/<id>.snapshot.ts`. The picker (T3.16) offers every entry of
 * {@link STARTER_TEMPLATES}; `loadStarterTemplate` applies the snapshot to a
 * fresh site's doc. An entry with no snapshot yet (still `tag: "Coming
 * soon"`) seeds nothing — the page gets the editor's default section as
 * before.
 *
 * The `templateId` is also passed to `api.createSite({ name, templateId })`.
 */
import * as Y from "yjs";

export interface StarterTemplate {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly preview: string; // emoji placeholder until real screenshots
  readonly tag?: string; // e.g. "Coming soon" for not-yet-designed templates
}

export const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    id: "blank",
    label: "Blank",
    description: "Start from scratch with an empty canvas.",
    preview: "◻️",
  },
  {
    id: "landing",
    label: "Landing page",
    description: "Hero section, features, and a call-to-action.",
    preview: "🏠",
    tag: "Popular",
  },
  {
    id: "portfolio",
    label: "Portfolio",
    description: "Showcase your work with a clean gallery layout.",
    preview: "🖼️",
    tag: "Coming soon",
  },
  {
    id: "small-business",
    label: "Small business",
    description: "Services, about section, and a contact form.",
    preview: "🏪",
    tag: "Coming soon",
  },
  {
    id: "coming-soon",
    label: "Coming soon",
    description: "Single-page teaser with an email signup.",
    preview: "⏳",
    tag: "Coming soon",
  },
];

/** Applied-snapshot origin: not in any UndoManager scope (a template is the
 *  site's starting point, not an undoable step), still persisted + synced. */
export const TEMPLATE_ORIGIN = Symbol.for("starter-template");

/** Lazy loaders — a snapshot is ~10 KB of base64 the picker never needs;
 *  only the chosen one is fetched, on first open. */
const SNAPSHOTS: Record<string, () => Promise<{ SNAPSHOT_B64: string }>> = {
  landing: () => import("./templates/landing.snapshot.js"),
};

export function hasStarterSnapshot(templateId: string): boolean {
  return templateId in SNAPSHOTS;
}

function fromBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Apply a starter template to an EMPTY doc. Returns false (and touches
 * nothing) when the template has no snapshot or the doc already holds pages —
 * the caller gates on the editor's `canSeed` (only the authoritative tab of a
 * brand-new site seeds; a follower or a synced peer never does).
 */
export async function loadStarterTemplate(
  ydoc: Y.Doc,
  templateId: string,
): Promise<boolean> {
  const load = SNAPSHOTS[templateId];
  if (!load || ydoc.getMap("nodes").size > 0) return false;
  const { SNAPSHOT_B64 } = await load();
  if (ydoc.getMap("nodes").size > 0) return false; // raced by a sync
  Y.applyUpdate(ydoc, fromBase64(SNAPSHOT_B64), TEMPLATE_ORIGIN);
  return true;
}
